/* eslint-disable no-shadow */
import { createSelector } from 'create-selector'
import { createBundle } from 'core/bundler'
import {
  TRAININGS_FETCH_STARTED,
  TRAININGS_FETCH_SUCCEEDED,
  TRAININGS_FETCH_FAILED,
  TRAININGS_CLEARED,
  TRAININGS_OUTDATED,
  TRAININGS_EXPIRED,
  USER_LOGGED_OUT,
  TRAINING_SELECTED,
  TRAINING_INSPECTED,
} from 'core/actiontypes'

const name = 'trainings' // e.g notification, trainings, users
const uCaseName = name.charAt(0).toUpperCase() + name.slice(1)
const defaultConfig = {
  apiRoute: '/trainings',
  stale: 900000, // fifteen minutes
  retry: 60000, // one minute,
  expireAfter: Infinity,
  persist: false,
  // checkIfOnline: true,
}

const initialState = {
  data: null,
  errorTimes: [],
  error: null,
  lastSuccess: null,
  isOutdated: false,
  isLoading: false,
  isExpired: false,
  failedPermanently: false,
  trainingLogs: [],
  trainingLogsLoading: false,
  trainingLogsError: null,
  selectedTraining: null,
  inspectedTraining: null,
}

const singularString = uCaseName.slice(0, uCaseName.length - 1)

const rawSelector = `select${uCaseName}Raw`
const dataSelector = `select${uCaseName}`
const selectedSelector = `selectSelected${singularString}`
const inspectedSelector = `selectInspected${singularString}`
const lastSuccessSelector = `select${uCaseName}LastSuccess`
const isExpiredSelector = `select${uCaseName}IsExpired`
const lastErrorSelector = `select${uCaseName}LastError`
const isStaleSelector = `select${uCaseName}IsStale`
const isWaitingToRetrySelector = `select${uCaseName}IsWaitingToRetry`
const isLoadingSelector = `select${uCaseName}IsLoading`
const failedPermanentlySelector = `select${uCaseName}FailedPermanently`
const shouldUpdateSelector = `select${uCaseName}ShouldUpdate`
const trainingLogsSelector = `select${uCaseName}TrainingLogs`
const trainingLogsLoaderSelector = `select${uCaseName}TrainingLogsLoading`
const trainingLogsErrorSelector = `select${uCaseName}TrainingLogsError`

const fetchSuccess = payload => ({ type: TRAININGS_FETCH_SUCCEEDED, payload })
const fetchError = error => ({ type: TRAININGS_FETCH_FAILED, error })

export default config => {
  // eslint-disable-next-line no-param-reassign
  config = { ...defaultConfig, ...config }
  const markDataAsOutdated = () => ({ type: TRAININGS_OUTDATED })
  const clearData = () => ({ type: TRAININGS_CLEARED })
  const expireData = () => ({ type: TRAININGS_EXPIRED })
  const fetchData =
    () =>
    (dispatch, { api }) => {
      dispatch({ type: TRAININGS_FETCH_STARTED })
      return api(config.apiRoute).then(
        payload => {
          // dispatch(fetchSuccess([...payload.owned]))
          dispatch(fetchSuccess(payload))
        },
        error => {
          dispatch(fetchError(error))
        }
      )
    }
  const stopTraining =
    trainingID =>
    (dispatch, { api }) => {
      api(`/trainings/${trainingID}/stop`, { method: 'POST' }).then(() => {
        dispatch(markDataAsOutdated())
      })
    }
  const editTraining =
    payload =>
    (dispatch, { api }) =>
      api(`/trainings/${payload.id}`, {
        method: 'PATCH',
        body: { name: payload.name },
      }).then(() => dispatch(markDataAsOutdated()))
  const deleteTraining =
    trainingID =>
    (dispatch, { api }) => {
      api(`/trainings/${trainingID}`, {
        method: 'DELETE',
      }).then(() => {
        dispatch(markDataAsOutdated())
      })
    }
  const setSelectedTraining = payload => ({
    type: TRAINING_SELECTED,
    payload,
  })
  const setInspectedTraining = payload => ({
    type: TRAINING_INSPECTED,
    payload,
  })

  const selectors = {
    [rawSelector]: state => state[name],
    [dataSelector]: createSelector(rawSelector, root => root.data),
    [selectedSelector]: createSelector(rawSelector, state => state.selectedTraining),
    [inspectedSelector]: createSelector(
      rawSelector,
      state => state.inspectedTraining
    ),
    [isStaleSelector]: createSelector(
      rawSelector, // raw - everything
      lastSuccessSelector, //  ???
      'selectAppTime',
      (state, time, appTime) => {
        if (state.isOutdated) {
          return true
        }
        if (!time) {
          return false
        }
        return appTime - time > config.stale
      }
    ),
    [isExpiredSelector]: createSelector(rawSelector, root => root.isExpired),
    [lastErrorSelector]: createSelector(
      rawSelector,
      resource => resource.errorTimes.slice(-1)[0] || null
    ),
    [lastSuccessSelector]: createSelector(rawSelector, root => root.lastSuccess),
    [isWaitingToRetrySelector]: createSelector(
      lastErrorSelector,
      'selectAppTime',
      (time, appTime) => {
        if (!time) {
          return false
        }
        return appTime - time < config.retry
      }
    ),
    [isLoadingSelector]: createSelector(
      rawSelector,
      resourceState => resourceState.isLoading
    ),
    [failedPermanentlySelector]: createSelector(
      rawSelector,
      resourceState => resourceState.failedPermanently
    ),
    [shouldUpdateSelector]: createSelector(
      isLoadingSelector,
      failedPermanentlySelector,
      isWaitingToRetrySelector,
      dataSelector,
      isStaleSelector,
      // 'selectIsOnline',
      (
        isLoading,
        failedPermanently,
        isWaitingToRetry,
        data,
        isStale
        // isOnline
      ) => {
        if (
          // (checkIfOnline && !isOnline) ||
          isLoading ||
          failedPermanently ||
          isWaitingToRetry
        ) {
          return false
        }
        if (data === null) {
          return true
        }
        return isStale
      }
    ),
    [trainingLogsSelector]: createSelector(
      rawSelector,
      resource => resource.trainingLogs
    ),
    [trainingLogsLoaderSelector]: createSelector(
      rawSelector,
      resource => resource.trainingLogsLoading
    ),
    [trainingLogsErrorSelector]: createSelector(
      rawSelector,
      resource => resource.trainingLogsError
    ),
    reactMissingTrainings: createSelector(
      'selectUser',
      shouldUpdateSelector,
      (user, update) => {
        if (update && user) {
          return fetchData()
        }
        return null
      }
    ),
    reactSelectedTrainingNonExistent: createSelector(
      selectedSelector,
      dataSelector,
      (selected, data) => {
        if (selected && !data?.find(training => training.id === selected)) {
          return setSelectedTraining(null)
        }
        return null
      }
    ),
    reactInspectedTrainingNonExistent: createSelector(
      inspectedSelector,
      dataSelector,
      (inspected, data) => {
        if (inspected && !data?.find(training => training.id === inspected)) {
          return setInspectedTraining(null)
        }
        return null
      }
    ),
  }

  if (config.expireAfter !== Infinity) {
    selectors[`reactExpire${uCaseName}`] = createSelector(
      lastSuccessSelector,
      'selectAppTime',
      (time, appTime) => {
        if (!time) {
          return null
        }
        if (appTime - time > config.expireAfter) {
          return expireData()
        }
        return null
      }
    )
  }

  return createBundle({
    name,
    reducer: (state = initialState, { type, payload, error, merge }) => {
      if (type === TRAININGS_FETCH_STARTED) {
        return { ...state, isLoading: true }
      }
      if (type === TRAININGS_FETCH_SUCCEEDED) {
        const updatedData = merge ? { ...state.data, ...payload } : payload
        return {
          ...state,
          isLoading: false,
          data: updatedData,
          lastSuccess: Date.now(),
          errorTimes: [],
          error: null,
          failedPermanently: false,
          isOutdated: false,
          isExpired: false,
        }
      }
      if (type === TRAININGS_FETCH_FAILED) {
        const errorMessage = (error && error.message) || error
        return {
          ...state,
          isLoading: false,
          errorTimes: [...state.errorTimes, Date.now()],
          error: errorMessage,
          failedPermanently: !!(error && error.permanent),
        }
      }
      if (type === TRAININGS_CLEARED || type === USER_LOGGED_OUT) {
        return initialState
      }
      if (type === TRAININGS_EXPIRED) {
        return {
          ...initialState,
          isExpired: true,
          errorTimes: state.errorTimes,
          error: state.error,
        }
      }
      if (type === TRAININGS_OUTDATED) {
        return { ...state, isOutdated: true }
      }
      if (type === TRAINING_SELECTED) {
        return { ...state, selectedTraining: payload }
      }
      if (type === TRAINING_INSPECTED) {
        return { ...state, inspectedTraining: payload }
      }
      return state
    },
    selectors,
    actions: {
      [`fetch${uCaseName}`]: fetchData,
      [`mark${uCaseName}AsOutdated`]: markDataAsOutdated,
      [`clear${uCaseName}`]: clearData,
      [`expire${uCaseName}`]: expireData,
      editTraining,
      deleteTraining,
      stopTraining,
      setSelectedTraining,
      setInspectedTraining,
    },
    init: store => {
      store.serverEvent('TRAINING_STARTED', ({ data }, store) => {
        store.markTrainingsAsOutdated()
      })
      store.serverEvent('TRAINING_FINISHED', ({ data }, store) => {
        store.markTrainingsAsOutdated()
      })
      store.serverEvent('TRAINING_FAILED', ({ data }, store) => {
        store.markTrainingsAsOutdated()
      })
    },
    args: null,
    middleware: null,
    persist: config.persist
      ? [
          TRAININGS_FETCH_SUCCEEDED,
          TRAININGS_EXPIRED,
          TRAININGS_OUTDATED,
          TRAININGS_CLEARED,
        ]
      : null,
  })
}
